import { Injectable } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';

import { ArmyBuilderConfig } from '../config';
import { HttpClientWithInFlightCache } from '../httpClient';

/**
 * Resolves image URLs from image document IDs and handles user-image uploads.
 *
 * Images are stored in S3 (via the API's `/images` module) and served through
 * `GET /api/images/:id`. This service constructs the URL from an imageId so
 * that components never need to know about the storage backend.
 *
 * ## Template images
 *   imageId on entity  →  ImageService.url(id)  →  <img [src]>
 *
 * ## User-uploaded images
 *   Users can take/upload photos per-unit-in-force (subscriber-only).
 *   The `userImageId` lives on the Unit instance (not the template).
 *   Shared forces strip `userImageId` server-side to prevent abuse.
 *   This service resolves both template and user image IDs the same way.
 *
 * ## CDN migration path
 *   When a CloudFront distribution is placed in front of the S3 bucket, only
 *   this service changes — all consumers still call `url(imageId)`.
 */
@Injectable({ providedIn: 'root' })
export class ImageService {
    constructor(
        private config: ArmyBuilderConfig,
        private http: HttpClientWithInFlightCache
    ) {}

    /**
     * Build a URL for the given image document _id.
     * Returns null if imageId is falsy.
     */
    url(imageId: string | null | undefined): string | null {
        if (!imageId) return null;

        let url = `${this.config.apiBaseUrl}/images/${imageId}`;
        return url;
    }

    /**
     * Upload a user photo for a unit.
     * The server resizes to 512px, converts to webp, and strips EXIF.
     * Returns `{ id }` — the stored-file document ID to save on the unit.
     */
    uploadUserImage(gameId: string, blob: Blob): Observable<{ id: string }> {
        const formData = new FormData();
        formData.append('file', blob, 'photo.png');
        formData.append('gameId', gameId);

        const url = `${this.config.apiBaseUrl}/images/user/upload`;
        return this.http.post(url, formData, { requiresLogin: true });
    }

    /**
     * Upload an event banner image (landscape, up to 1200px wide, 2 MB max).
     * Returns `{ id }` — the stored-file document ID to save on the event.
     */
    uploadEventBanner(gameId: string, blob: Blob): Observable<{ id: string }> {
        const formData = new FormData();
        formData.append('file', blob, 'event-banner.png');
        formData.append('gameId', gameId);

        const url = `${this.config.apiBaseUrl}/images/event-banner/upload`;
        return this.http.post(url, formData, { requiresLogin: true });
    }

    /**
     * Upload an event pack PDF (up to 10 MB).
     * Returns `{ id }` — the stored-file document ID to save on the event.
     */
    uploadEventPack(gameId: string, file: File): Observable<{ id: string }> {
        const formData = new FormData();
        formData.append('file', file, file.name);
        formData.append('gameId', gameId);

        const url = `${this.config.apiBaseUrl}/images/event-pack/upload`;
        return this.http.post(url, formData, { requiresLogin: true });
    }

    /**
     * Delete a user-uploaded image by its stored-file document ID.
     * The server verifies ownership before removing from S3 + DB.
     */
    deleteUserImage(imageId: string): Observable<void> {
        const url = `${this.config.apiBaseUrl}/images/user/${imageId}`;
        return this.http.delete(url, null, { requiresLogin: true });
    }

    /**
     * Fetch a user-owned image as a blob and return an object URL.
     *
     * User images require JWT authentication for the server to authorise the
     * request. A plain `<img [src]>` tag makes an unauthenticated browser
     * request, which the server rejects with 403 before setting CORP headers —
     * causing Capacitor's WebView to raise ERR_BLOCKED_BY_RESPONSE.NotSameOrigin.
     * Fetching through Angular's HttpClient attaches the Bearer token and avoids
     * that entirely.
     */
    private failedImageIds = new Set<string>();

    fetchUserImage(imageId: string): Observable<string | null> {
        if (!imageId) return of(null);
        if (this.failedImageIds.has(imageId)) return of(null);
        const url = `${this.config.apiBaseUrl}/images/${imageId}`;
        return this.http.get(url, { responseType: 'blob', requiresLogin: true }).pipe(
            map((blob: Blob) => URL.createObjectURL(blob)),
            catchError(() => {
                this.failedImageIds.add(imageId);
                return of(null);
            })
        );
    }

    /**
     * Register a usage reference on the stored-file record for the given image.
     * Idempotent — duplicate refs are ignored by the server.
     * Should be called after saving any entity that has an image field.
     */
    addEntityRef(imageId: string, entityId: string, entityType: string): Observable<any> {
        const url = `${this.config.apiBaseUrl}/images/admin/${imageId}/entity-refs`;
        return this.http.post(url, { entityId, entityType }, { requiresLogin: true });
    }

    /**
     * Remove a usage reference from the stored-file record.
     * Should be called when an entity's image field is cleared or replaced.
     */
    removeEntityRef(imageId: string, entityId: string): Observable<any> {
        const url = `${this.config.apiBaseUrl}/images/admin/${imageId}/entity-refs/${entityId}`;
        return this.http.delete(url, null, { requiresLogin: true });
    }
}
